import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Clock, Plus, Minus } from "lucide-react";
import Layout, { Overline } from "../components/Layout";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Motion";
import Icon from "../components/Icons";
import { IMAGES, COURSES } from "../data/site";

const CourseCard = ({ c, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={(i % 3) * 0.06}>
      <div data-testid={`course-card-${i}`} className="group h-full rounded-3xl border border-charcoal/10 bg-white overflow-hidden hover:border-eucalyptus/40 transition-colors flex flex-col">
        <div className="relative h-44 overflow-hidden">
          <img src={c.image} alt={c.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent" />
          <span className="absolute top-4 left-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-warmwhite/90 backdrop-blur text-eucalyptus text-xl"><Icon name={c.icon} /></span>
          <span className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 text-xs font-medium text-warmwhite bg-charcoal/50 backdrop-blur px-3 py-1.5 rounded-full">
            <Clock size={12} /> {c.duration}
          </span>
        </div>
        <div className="p-6 flex flex-col flex-1">
          <h3 className="font-heading text-2xl font-semibold tracking-tight text-charcoal">{c.title}</h3>
          <p className="mt-2 text-charcoal/65 leading-relaxed text-sm flex-1">{c.overview}</p>

          <button
            data-testid={`course-toggle-${i}`}
            onClick={() => setOpen((v) => !v)}
            className="mt-5 inline-flex items-center justify-between w-full text-sm font-semibold text-eucalyptus border-t border-charcoal/10 pt-4"
          >
            Course details {open ? <Minus size={16} /> : <Plus size={16} />}
          </button>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden"
              >
                <div className="pt-4 space-y-4">
                  <div>
                    <p className="text-xs tracking-overline uppercase text-earth mb-2">Learning outcomes</p>
                    <ul className="space-y-1.5">
                      {c.outcomes.map((o) => (
                        <li key={o} className="text-sm text-charcoal/75 flex gap-2"><span className="text-ochre">—</span>{o}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs tracking-overline uppercase text-earth mb-1">Who should attend</p>
                    <p className="text-sm text-charcoal/75">{c.who}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <Link
            to={`/contact?enquiry=Training&subject=${encodeURIComponent(c.title)}`}
            data-testid={`course-enquire-${i}`}
            className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-eucalyptus text-warmwhite px-5 py-3 font-semibold hover:bg-eucalyptus-deep transition-colors"
          >
            Enquire <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </Reveal>
  );
};

export default function Training() {
  return (
    <Layout
      title="Training"
      description="Professional learning pathways for rangers — carbon, biodiversity monitoring, native seed, GIS, drone mapping, cultural heritage field skills and leadership, delivered on Country."
    >
      <PageHero
        overline="Professional Learning Pathways"
        titleLines={["Training that", "grows on Country"]}
        subtitle="Indigenous knowledge working alongside practical science. Every course is hands-on and built for real ranger work."
        image={IMAGES.rangersDrone}
      />

      <section className="py-24 lg:py-32 bg-warmwhite">
        <div className="mx-auto max-w-[88rem] px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="text-eucalyptus mb-6"><Overline>Courses</Overline></div>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tighter text-charcoal leading-[0.95]">
              Learning pathways, not one-off workshops.
            </h2>
            <p className="mt-5 text-lg text-charcoal/70 leading-relaxed">
              Each course stands on its own, and together they build complete ranger capability over time.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {COURSES.map((c, i) => <CourseCard key={c.title} c={c} i={i} />)}
          </div>
        </div>
      </section>

      <section className="py-24 bg-eucalyptus text-warmwhite relative overflow-hidden">
        <div className="grain absolute inset-0 opacity-30" />
        <div className="relative mx-auto max-w-[80rem] px-6 lg:px-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight max-w-xl">Build a training pathway tailored to your ranger team.</h2>
          <Link to="/contact" data-testid="training-cta" className="inline-flex items-center gap-2 rounded-full bg-warmwhite text-eucalyptus-deep px-7 py-4 font-semibold hover:bg-sandstone transition-colors shrink-0">
            Plan your training <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
